/**
 * `projectApplication` — application body in, display model out.
 *
 * This is the entry point both view pages call. The two pages are served by TWO
 * SEPARATE APIs — the applicant microsite has its own, the ASB-CAP admin app has
 * another — so they can and do receive different information. What they share is
 * the body shape and therefore the mapping, which is what lives here.
 *
 * The projection owns three decisions that must not be made per-host, because the
 * two hosts would answer them differently and the difference would be a privacy
 * bug or a lie about the applicant:
 *
 *  1. WHICH SECTIONS THIS AUDIENCE MAY SEE. Driven by the registry. Applicants see
 *     every section without exception — it is their own information. Staff
 *     visibility is narrower and will become role-dependent.
 *
 *  2. WHY A SECTION HAS NOTHING. `withheld` and `not_provided` are different
 *     claims, and only the caller knows which applies, so the projection never
 *     guesses. Withholding is staff-side only; see `StaffProjectionOptions`.
 *
 *  3. WHETHER A SECTION WAS ANSWERED AT ALL. See `sectionRawValue` below.
 *
 * PURE BY DESIGN: no Angular, no DOM, no services. The hosts own the chrome.
 */

import type { IOasisApplicationBase, ProgramEligibility, ProgramType, SlipTrack } from '../oasis-application.types';
import type { ApplicationDisplayModel, DisplayBlock, DisplaySection, DisplaySectionState } from './oasis-display.types';
import { sectionsForAudience } from './oasis-section-registry';
import {
	projectCitizenship,
	projectComponentsOfInterest,
	projectInstructions,
	projectPersonalInformation,
	projectProgramEligibility,
} from './projectors/identity.projectors';
import { projectBarAdmission, projectEducationExperience } from './projectors/education.projectors';
import {
	projectAdditionalInformation,
	projectCertifyAndSubmit,
	projectDemographics,
	projectEmployment,
	projectEssays,
	projectHonorsAndAwards,
	projectLanguages,
	projectMilitaryService,
	projectProfessionalAccomplishments,
	projectReferences,
	projectSupportingDocuments,
} from './projectors/experience.projectors';

/**
 * The body plus the program discriminants.
 *
 * `program`, `slipTrack`, and `programEligibility` are NOT declared on
 * `IOasisApplicationBase` — they live on the `OasisApplication` union — but they
 * are present on every real stored body. They are optional here so a caller
 * holding only the base type can still project, which is what both view pages
 * have (the API returns `OasisApplicationView.body` typed as the base).
 */
export type ProjectableApplicationBody = IOasisApplicationBase & {
	program?: ProgramType;
	slipTrack?: SlipTrack;
	programEligibility?: ProgramEligibility;
};

interface ProjectionOptionsBase {
	/**
	 * Keep sections the applicant never saved, so the reader sees the full outline
	 * and absence is visible. Defaults to `true`: for a reviewer, a section being
	 * unanswered is itself information, and for an applicant the outline doubles as
	 * a completeness check.
	 */
	includeEmptySections?: boolean;
}

/**
 * Applicant-side options.
 *
 * There is deliberately NO withholding here. An applicant is reading their own
 * application, so there is nothing to keep from them — every section is always
 * visible. The applicant microsite has its own API, which has no reason to strip
 * sections and therefore no reason to report having stripped any.
 *
 * What IS softened for applicants is the meta layer around these sections —
 * workflow status and component lane wording, so they are not shown the sausage
 * being made. That lives in `appStatusLabel` / `componentStatusLabel`, not here.
 */
export interface ApplicantProjectionOptions extends ProjectionOptionsBase {
	audience: 'applicant';
}

/** Staff-side options, where visibility is role-dependent and can be restricted. */
export interface StaffProjectionOptions extends ProjectionOptionsBase {
	audience: 'staff';
	/**
	 * Section keys the ADMIN API removed from the response because this requester's
	 * role does not permit them. Rendered as `withheld`, which is a different
	 * statement from "not provided".
	 *
	 * Populate this from the API's own report of what it stripped. Do NOT infer it
	 * from a missing key: absence is ambiguous, which is the whole reason this
	 * option exists.
	 *
	 * Staff-only by construction. The admin app and the applicant microsite are
	 * served by SEPARATE APIs, so this concern does not exist on the applicant side
	 * and the type prevents a caller from pretending otherwise.
	 */
	withheldSections?: readonly string[];
}

export type ProjectApplicationOptions = ApplicantProjectionOptions | StaffProjectionOptions;

export function projectApplication(body: ProjectableApplicationBody | null | undefined, options: ProjectApplicationOptions): ApplicationDisplayModel {
	if (!body) return { sections: [] };

	const includeEmpty = options.includeEmptySections ?? true;
	// Narrowed on `audience`: withholding is a staff-side concept only.
	const withheld = new Set(options.audience === 'staff' ? (options.withheldSections ?? []) : []);
	const sections: DisplaySection[] = [];

	for (const descriptor of sectionsForAudience(options.audience)) {
		if (withheld.has(descriptor.key)) {
			sections.push({ key: descriptor.key, label: descriptor.label, state: 'withheld', blocks: [] });
			continue;
		}

		const raw = sectionRawValue(descriptor.key, body);
		const state: DisplaySectionState = raw === undefined ? 'not_provided' : 'answered';

		if (state === 'not_provided' && !includeEmpty) continue;

		sections.push({
			key: descriptor.key,
			label: descriptor.label,
			state,
			blocks: state === 'answered' ? projectSection(descriptor.key, body) : [],
		});
	}

	return { sections };
}

/**
 * The body slice a section renders, or `undefined` when the body never carried it.
 *
 * PRESENCE IS THE ANSWER SIGNAL, and it is a real one rather than a guess. The
 * applicant form's store starts as `{}` and `saveSection` merges in exactly one
 * section key per save, so a key exists if and only if the applicant saved that
 * section. The substantive-change cascade `delete`s keys when an upstream answer
 * invalidates them, so a cleared section correctly reverts to absent.
 *
 * This is what lets "worked through all 35 accomplishment questions and claimed
 * none" read differently from "never opened the section". The first persists as a
 * present object with every item `selected: false` — `buildSectionData` writes the
 * full group unconditionally — and projects to a visible "0 of 35". The second is
 * simply absent. Deriving state from whether the projected content is non-empty
 * would collapse those two, which is a distinction a reviewer assessing
 * completeness genuinely needs.
 *
 * `undefined` rather than a falsy check is deliberate: `additionalInformation: ''`
 * and `languages: []` are answers ("I have nothing to add", "I speak no other
 * languages"), not absences.
 */
function sectionRawValue(key: string, body: ProjectableApplicationBody): unknown {
	// Program eligibility is not a field on the base body — it and the program
	// discriminants ride alongside it — so presence means either one is set.
	if (key === 'programEligibility') {
		return body.programEligibility ?? body.program;
	}

	return (body as unknown as Record<string, unknown>)[key];
}

/**
 * Dispatches one section to its projector.
 *
 * Only called for sections the body actually carries, so each projector can
 * assume its slice exists — but not that it is complete, because draft saves
 * persist partially-filled sections.
 */
function projectSection(key: string, body: ProjectableApplicationBody): DisplayBlock[] {
	switch (key) {
		case 'instructionsAndAcknowledgements':
			return body.instructionsAndAcknowledgements ? projectInstructions(body.instructionsAndAcknowledgements) : [];
		case 'personalInformation':
			return body.personalInformation ? projectPersonalInformation(body.personalInformation) : [];
		case 'citizenship':
			return body.citizenship ? projectCitizenship(body.citizenship) : [];
		case 'programEligibility':
			return projectProgramEligibility(body.programEligibility, body.program, body.slipTrack);
		case 'componentsOfInterest':
			return body.componentsOfInterest ? projectComponentsOfInterest(body.componentsOfInterest) : [];
		case 'educationExperience':
			return body.educationExperience ? projectEducationExperience(body.educationExperience) : [];
		case 'barAdmission':
			return body.barAdmission ? projectBarAdmission(body.barAdmission) : [];
		case 'professionalAccomplishments':
			return body.professionalAccomplishments ? projectProfessionalAccomplishments(body.professionalAccomplishments) : [];
		case 'honorsAndAwards':
			return body.honorsAndAwards ? projectHonorsAndAwards(body.honorsAndAwards) : [];
		case 'employment':
			return body.employment ? projectEmployment(body.employment) : [];
		case 'references':
			return body.references ? projectReferences(body.references) : [];
		case 'militaryService':
			return body.militaryService ? projectMilitaryService(body.militaryService) : [];
		case 'essays':
			return body.essays ? projectEssays(body.essays) : [];
		case 'languages':
			return body.languages ? projectLanguages(body.languages) : [];
		case 'additionalInformation':
			return projectAdditionalInformation(body.additionalInformation);
		case 'demographics':
			return body.demographics ? projectDemographics(body.demographics) : [];
		case 'supportingDocuments':
			return body.supportingDocuments ? projectSupportingDocuments(body.supportingDocuments) : [];
		case 'certifyAndSubmit':
			return body.certifyAndSubmit ? projectCertifyAndSubmit(body.certifyAndSubmit) : [];
		default:
			return [];
	}
}
